// Footer içerik sayfalarının hafif biçimlendirmesi.
//
// İçerik panelde **düz metin** olarak yazılır ve öyle saklanır: yöneticinin
// HTML bilmesi gerekmez, metin arama motoruna ve panele olduğu gibi girer.
// Ama kapsamlı bir kural metni başlıksız ve maddesiz okunmuyor — sayfa bir
// duvar oluyordu. Bu yüzden metin çizilmeden önce bloklara ayrılır:
//
//   ## Başlık          → bölüm başlığı
//   ### Alt başlık     → alt başlık
//   - madde            → madde listesi ("•" ve "*" de kabul edilir)
//   1. madde           → numaralı liste
//   boş satır          → paragraf ayracı
//
// Satır içinde `**kalın**` çalışır; adres ve e-posta kendiliğinden bağlantıya
// dönüşür ({{site_url}} / {{contact_email}} yer tutucuları tıklanabilir
// olmalıdır — bkz. lib/textTemplate.js).
//
// Çıktı **React düğümü değil veridir**: `dangerouslySetInnerHTML` bilinçli
// olarak kullanılmaz, panelden gelen metin hiçbir yolda HTML olarak
// yorumlanmaz.

const HEADING_RE = /^(#{2,3})\s+(.*)$/;
const BULLET_RE = /^\s*[-•*]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d{1,2})[.)]\s+(.*)$/;

const BOLD_RE = /\*\*([^*]+)\*\*/g;
// Adres ve e-posta. Sondaki noktalama (cümle sonu, parantez) bağlantıya
// girmez: "adresimiz https://ornek.com." yazıldığında nokta da linke girerse
// adres 404 açardı.
const LINK_RE = /((?:https?:\/\/|www\.)[^\s<>()]+|[^\s<>()]+@[^\s<>()]+\.[a-z]{2,})/gi;
const TRAILING = /[.,;:!?'"»)\]]+$/;

/** Satır içi parçalar: düz metin, kalın metin ve bağlantılar. */
export const parseInline = (value) => {
  const source = String(value ?? '');
  const parts = [];
  let cursor = 0;

  const pushLinked = (text, bold) => {
    if (!text) return;
    let index = 0;
    LINK_RE.lastIndex = 0;
    let match = LINK_RE.exec(text);
    while (match) {
      const raw = match[0].replace(TRAILING, '');
      if (!raw) {
        match = LINK_RE.exec(text);
        continue;
      }
      if (match.index > index) {
        parts.push({ type: 'text', text: text.slice(index, match.index), bold });
      }
      const href = raw.includes('@') && !/^https?:/i.test(raw)
        ? `mailto:${raw}`
        : (/^www\./i.test(raw) ? `https://${raw}` : raw);
      parts.push({ type: 'link', text: raw, href, bold });
      index = match.index + raw.length;
      LINK_RE.lastIndex = index;
      match = LINK_RE.exec(text);
    }
    if (index < text.length) {
      parts.push({ type: 'text', text: text.slice(index), bold });
    }
  };

  BOLD_RE.lastIndex = 0;
  let bold = BOLD_RE.exec(source);
  while (bold) {
    if (bold.index > cursor) pushLinked(source.slice(cursor, bold.index), false);
    pushLinked(bold[1], true);
    cursor = bold.index + bold[0].length;
    bold = BOLD_RE.exec(source);
  }
  pushLinked(source.slice(cursor), false);
  return parts;
};

/** Düz metni çizilebilir bloklara ayırır. */
export const parsePageContent = (value) => {
  const lines = String(value ?? '').split('\n');
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: 'paragraph', text: paragraph.join('\n') });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push(list);
    list = null;
  };
  const flush = () => { flushParagraph(); flushList(); };

  lines.forEach((line) => {
    if (!line.trim()) {
      flush();
      return;
    }
    const heading = HEADING_RE.exec(line);
    if (heading) {
      flush();
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        text: heading[2].trim(),
      });
      return;
    }
    const ordered = ORDERED_RE.exec(line);
    if (ordered) {
      flushParagraph();
      if (!list || list.ordered !== true) {
        flushList();
        list = { type: 'list', ordered: true, items: [] };
      }
      list.items.push(ordered[2].trim());
      return;
    }
    const bullet = BULLET_RE.exec(line);
    if (bullet) {
      flushParagraph();
      if (!list || list.ordered !== false) {
        flushList();
        list = { type: 'list', ordered: false, items: [] };
      }
      list.items.push(bullet[1].trim());
      return;
    }
    flushList();
    paragraph.push(line.trim());
  });

  flush();
  return blocks;
};
